import React, { useState, useEffect } from "react";
import {
  Phone,
  CheckCircle,
  AlertTriangle,
  PhoneCall,
  Heart,
  Activity,
  Zap,
  Bell,
  User,
  Clock,
} from "lucide-react";

interface Notification {
  id: string;
  patient: string;
  room: string;
  type:
    | "Code Blue"
    | "Rapid Response"
    | "Fall Alert"
    | "IV Alarm"
    | "Nurse Call";
  severity: "critical" | "high" | "medium";
  time: string;
  vitals?: {
    hr: number;
    spo2: number;
  };
}

const SmartwatchDemo: React.FC = () => {
  const [currentNotification, setCurrentNotification] =
    useState<Notification | null>(null);
  const [isVibrating, setIsVibrating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [actionTaken, setActionTaken] = useState<string | null>(null);
  const [notificationHistory, setNotificationHistory] = useState<
    Array<Notification & { action: string; timestamp: Date }>
  >([]);

  const mockNotifications: Notification[] = [
    {
      id: "1",
      patient: "John Smith",
      room: "ICU-204",
      type: "Code Blue",
      severity: "critical",
      time: "2 min ago",
      vitals: { hr: 45, spo2: 82 },
    },
    {
      id: "2",
      patient: "Mary Johnson",
      room: "MED-312",
      type: "Fall Alert",
      severity: "high",
      time: "1 min ago",
    },
    {
      id: "3",
      patient: "Robert Davis",
      room: "ER-108",
      type: "Rapid Response",
      severity: "high",
      time: "Just now",
      vitals: { hr: 132, spo2: 88 },
    },
    {
      id: "4",
      patient: "Lisa Wilson",
      room: "ICU-201",
      type: "IV Alarm",
      severity: "medium",
      time: "30 sec ago",
    },
  ];

  const triggerNotification = (notification: Notification) => {
    setCurrentNotification(notification);
    setIsVibrating(true);
    setShowSuccess(false);
    setActionTaken(null);

    setTimeout(() => setIsVibrating(false), 2000);
  };

  const handleAction = (action: "accept" | "escalate" | "callback") => {
    if (!currentNotification) return;

    setActionTaken(action);
    setShowSuccess(true);

    // Add to history
    setNotificationHistory((prev) => [
      ...prev,
      {
        ...currentNotification,
        action: action,
        timestamp: new Date(),
      },
    ]);

    setTimeout(() => {
      setCurrentNotification(null);
      setShowSuccess(false);
      setActionTaken(null);
    }, 2000);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "bg-red-600";
      case "high":
        return "bg-orange-500";
      case "medium":
        return "bg-yellow-500";
      default:
        return "bg-blue-500";
    }
  };

  const getAlarmIcon = (type: string) => {
    switch (type) {
      case "Code Blue":
        return <Heart className="w-6 h-6" />;
      case "Rapid Response":
        return <Activity className="w-6 h-6" />;
      case "Fall Alert":
        return <AlertTriangle className="w-6 h-6" />;
      case "IV Alarm":
        return <Zap className="w-6 h-6" />;
      default:
        return <Bell className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Smartwatch Notification Demo
        </h1>
        <p className="text-gray-600 mb-8">
          Experience how nurses receive and respond to critical alerts on their
          smartwatch
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Smartwatch Display */}
          <div className="flex flex-col items-center">
            <div className="relative">
              {/* Watch Frame */}
              <div className="bg-gray-800 rounded-[3rem] p-4 shadow-2xl">
                {/* Watch Screen */}
                <div
                  className={`bg-black rounded-[2.5rem] w-80 h-96 relative overflow-hidden transition-all duration-300 ${
                    isVibrating ? "animate-pulse ring-4 ring-red-500" : ""
                  }`}
                >
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 bg-gray-900 px-6 py-2 flex justify-between items-center text-white text-xs">
                    <span>9:41 AM</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span>87%</span>
                    </div>
                  </div>

                  {/* Notification Content */}
                  <div className="h-full flex items-center justify-center p-6 pt-12">
                    {!currentNotification ? (
                      <div className="text-center text-gray-400">
                        <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg">No active alerts</p>
                        <p className="text-sm mt-2">
                          Tap a test notification →
                        </p>
                      </div>
                    ) : showSuccess ? (
                      <div className="text-center animate-fade-in">
                        <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-500" />
                        <p className="text-white text-xl font-semibold">
                          {actionTaken === "accept" && "Accepted"}
                          {actionTaken === "escalate" && "Escalated"}
                          {actionTaken === "callback" && "Calling..."}
                        </p>
                      </div>
                    ) : (
                      <div className="w-full animate-slide-up">
                        {/* Alert Header */}
                        <div
                          className={`${getSeverityColor(
                            currentNotification.severity
                          )} rounded-t-2xl p-4 text-white`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {getAlarmIcon(currentNotification.type)}
                              <div>
                                <p className="font-bold text-lg">
                                  {currentNotification.type}
                                </p>
                                <p className="text-sm opacity-90">
                                  {currentNotification.time}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Patient Info */}
                        <div className="bg-gray-900 p-4 text-white">
                          <div className="flex items-center space-x-3 mb-3">
                            <User className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-semibold">
                                {currentNotification.patient}
                              </p>
                              <p className="text-sm text-gray-400">
                                {currentNotification.room}
                              </p>
                            </div>
                          </div>

                          {currentNotification.vitals && (
                            <div className="bg-gray-800 rounded-lg p-3 grid grid-cols-2 gap-3">
                              <div className="text-center">
                                <p className="text-xs text-gray-400">HR</p>
                                <p
                                  className={`text-lg font-bold ${
                                    currentNotification.vitals.hr < 60 ||
                                    currentNotification.vitals.hr > 100
                                      ? "text-red-400"
                                      : "text-green-400"
                                  }`}
                                >
                                  {currentNotification.vitals.hr}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-gray-400">SpO2</p>
                                <p
                                  className={`text-lg font-bold ${
                                    currentNotification.vitals.spo2 < 90
                                      ? "text-red-400"
                                      : "text-green-400"
                                  }`}
                                >
                                  {currentNotification.vitals.spo2}%
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="bg-gray-900 rounded-b-2xl p-4 grid grid-cols-3 gap-2">
                          <button
                            onClick={() => handleAction("accept")}
                            className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors flex flex-col items-center space-y-1"
                          >
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-xs">Accept</span>
                          </button>
                          <button
                            onClick={() => handleAction("escalate")}
                            className="bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-xl font-semibold transition-colors flex flex-col items-center space-y-1"
                          >
                            <AlertTriangle className="w-5 h-5" />
                            <span className="text-xs">Escalate</span>
                          </button>
                          <button
                            onClick={() => handleAction("callback")}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-colors flex flex-col items-center space-y-1"
                          >
                            <PhoneCall className="w-5 h-5" />
                            <span className="text-xs">Call</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Watch Band */}
              <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-700 w-20 h-16 rounded-t-3xl"></div>
              <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-700 w-20 h-16 rounded-b-3xl"></div>
            </div>

            {/* Features */}
            <div className="mt-8 bg-white rounded-xl p-6 shadow-lg w-full max-w-md">
              <h3 className="font-semibold text-gray-900 mb-3">Key Features</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Haptic feedback for urgent alerts</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Glove-friendly one-tap actions</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>At-a-glance patient context</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Critical vitals display</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Wear OS & watchOS compatible</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Test Notifications Panel */}
          <div>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                Test Notifications
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Click to simulate incoming alerts
              </p>
              <div className="space-y-3">
                {mockNotifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => triggerNotification(notification)}
                    className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-colors border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`${getSeverityColor(
                            notification.severity
                          )} text-white p-2 rounded-lg`}
                        >
                          {getAlarmIcon(notification.type)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {notification.type}
                          </p>
                          <p className="text-sm text-gray-600">
                            {notification.patient} • {notification.room}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {notification.time}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Response History */}
            {notificationHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Response History
                </h3>
                <div className="space-y-2">
                  {notificationHistory
                    .slice(-5)
                    .reverse()
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-sm p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              item.action === "accept"
                                ? "bg-green-500"
                                : item.action === "escalate"
                                ? "bg-orange-500"
                                : "bg-blue-500"
                            }`}
                          ></div>
                          <div>
                            <span className="font-medium">{item.type}</span>
                            <span className="text-gray-500 ml-2">
                              {item.patient}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-600 capitalize">
                            {item.action}
                          </p>
                          <p className="text-xs text-gray-400">
                            {item.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartwatchDemo;
